import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { getUserPrivacyChoicesUrl } from '../../constants/legalUrls';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="section" sx={{ mb: 3 }}>
    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" component="div" sx={{ '& p': { mb: 1.5 } }}>
      {children}
    </Typography>
  </Box>
);

const SubBlock: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2, pl: { xs: 0, sm: 1 }, borderLeft: { xs: 'none', sm: '3px solid' }, borderColor: 'primary.main' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" component="div">
      {children}
    </Typography>
  </Box>
);

/**
 * Public privacy policy for SkimBuster (web + mobile parity for App Store / storefront disclosures).
 * Aligns with typical “App Privacy” categories: contact info, location, user content, identifiers, usage, diagnostics.
 */
const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { colors } = useCustomTheme();
  const lastUpdated = 'May 6, 2026';

  const back = () => {
    if (currentUser) navigate(-1);
    else navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(165deg, ${colors.MainBackground} 0%, #0f172a 45%, ${colors.MainBackground} 100%)`,
        py: 3,
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={back}
          sx={{
            mb: 2,
            color: colors.TextPrimary,
            borderRadius: '50px',
            border: '1px solid rgba(34, 211, 238, 0.35)',
            '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.12)' },
          }}
        >
          {currentUser ? 'Back' : 'Back to sign in'}
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(34, 211, 238, 0.2)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 800, color: colors.TextPrimary }}>
            SkimBuster Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {lastUpdated}. English (U.S.)
          </Typography>

          <Section title="Summary">
            <p>
              SkimBuster helps organizations manage sites, devices, and skimming-related inspection reports. This policy
              describes how we collect, use, and share information when you use our services (including our web portal and
              mobile apps).
            </p>
          </Section>

          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

          <Section title="Contact information">
            <p>We process account and profile details needed to operate the service:</p>
            <SubBlock title="Name">
              Used for account identification, display in the product, and communications related to the service. Linked to
              your account.
            </SubBlock>
            <SubBlock title="Email address">
              Used for authentication, account recovery, operational notifications, and (where enabled) credential or
              security-related messages. Linked to your account.
            </SubBlock>
            <SubBlock title="Phone number">
              May be collected when you or your organization provide it for contact or verification. Linked to your account
              when supplied.
            </SubBlock>
            <SubBlock title="Physical address">
              May be collected for company or site records (for example, store or facility addresses associated with reports).
              May be linked to your organization or account depending on context.
            </SubBlock>
            <SubBlock title="Other contact information">
              Additional contact fields your organization chooses to store (for example, department or title). Linked to
              your profile when provided.
            </SubBlock>
          </Section>

          <Section title="Location">
            <SubBlock title="Precise location">
              When you submit a report or use device features that capture location, we may collect coordinates or
              location-derived fields to document where a scan or incident occurred. Some location context may be shown in
              maps or exports. Depending on product settings and report visibility, location may be linked to your account
              or organization, or processed in a more limited form for shared or public views as described in-app.
            </SubBlock>
          </Section>

          <Section title="User content">
            <SubBlock title="Photos or videos">
              Images you attach to reports or profiles (for example, evidence photos) are stored and used to provide the
              service, generate PDFs or exports you request, and share within your organization according to privacy settings.
              Linked to your account or organization.
            </SubBlock>
          </Section>

          <Section title="Identifiers">
            <SubBlock title="User ID">
              We assign and use unique account identifiers to secure sign-in, authorize access, and associate activity with
              the correct organization. Linked to your account.
            </SubBlock>
          </Section>

          <Section title="Usage data">
            <SubBlock title="Product interaction">
              We collect usage information needed to run the product (for example, feature usage, timestamps, and
              performance of requests) to maintain reliability, secure the service, and improve functionality. Linked to your
              account or device context where needed for security and auditing.
            </SubBlock>
          </Section>

          <Section title="Diagnostics">
            <SubBlock title="Crash data">
              Crash or error information may be collected to diagnose stability issues. May be linked to your account when
              error reports include account context.
            </SubBlock>
            <SubBlock title="Performance data">
              Technical metrics (such as latency or resource usage) may be processed to keep the service fast and available.
              May be linked in aggregated or identifiable form depending on the logging context.
            </SubBlock>
            <SubBlock title="Other diagnostic data">
              Additional technical logs may be used for security monitoring, fraud prevention, and troubleshooting.
            </SubBlock>
          </Section>

          <Section title="Data used for tracking (disclosure)">
            <p>
              Where required by platform rules, we disclose categories that <strong>may</strong> be used to track you across
              apps or websites owned by other companies. SkimBuster’s core purpose is organizational security and reporting;
              we do not sell your personal information. Limited analytics or diagnostics from our systems or embedded
              components may produce data that platforms classify as tracking-related. Where optional or organizational
              features rely on other companies, those parties process information under their own terms.
            </p>
          </Section>

          <Section title="How we share information">
            <p>
              We share data with a limited set of subprocessors and partners bound by confidentiality and security
              obligations, solely to operate, secure, and improve the service. We do not publish a technical inventory of those
              systems publicly. Within the product, your organization’s admins control many sharing settings (such as report
              visibility).
            </p>
          </Section>

          <Section title="Retention & security">
            <p>
              We retain information as long as needed to provide the service, comply with law, resolve disputes, and enforce
              agreements. We use industry-standard safeguards; no method of transmission over the Internet is 100% secure.
            </p>
          </Section>

          <Box id="privacy-choices" sx={{ scrollMarginTop: 96 }}>
            <Section title="Your privacy choices">
              <p>
                Depending on your region, you may have rights to access, correct, delete, or export personal information, or
                to object to certain processing. Contact your <strong>organization administrator</strong> for account-level
                requests. For other inquiries, contact us at{' '}
                <Link href="mailto:privacy@micclo.com" color="primary">
                  privacy@micclo.com
                </Link>
                .
              </p>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                User Privacy Choices URL (optional in App Store Connect):{' '}
                <Link href={getUserPrivacyChoicesUrl()} color="primary">
                  {getUserPrivacyChoicesUrl()}
                </Link>
              </Typography>
            </Section>
          </Box>

          <Section title="Changes">
            <p>
              We may update this policy from time to time. The “Last updated” date will change when we do. Continued use after
              changes means you accept the updated policy.
            </p>
          </Section>

          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Typography variant="body2" color="text.secondary">
            SkimBuster is provided by Micclo. This policy supplements any agreement between your organization and Micclo.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
